import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';

import {
    buscarAreasConfiguracion,
    buscarOrganizadoresConfiguracion,
    buscarUbicacionesConfiguracion,
    obtenerPaquete
} from '../../../../actions/configuracion';
import { cambiarStatusOrden } from '../../../../actions/ordenesDevolucion';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const toOption = (item) => ({ value: item?._id || '', label: item?.nombre || 'Sin definir' });

const filaVacia = (idRow, cantidad) => ({
    rowId: `${idRow}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    area: null,
    organizador: null,
    ubicacion: null,
    cantidad
});

/* Solo se devuelven paquetes completos (producto=null, orden_salida_paquete definido en el
   renglón de OrdenDevolucionProducto) — pero el inventario sigue acreditándose producto por
   producto, así que aquí se expande cada paquete a sus componentes reales del catálogo para
   poder pedir una ubicación real por cada uno, igual que a los productos sueltos. */
const CompletarOrdenDevolucionModal = ({ show, onHide, orden, onSuccess }) => {
    const dispatch = useDispatch();

    const { areas } = useSelector(state => state.configuracion);

    const productosOrden = useMemo(() => {
        const lista = Array.isArray(orden?.productos) ? orden.productos : [];
        return lista.filter(p => !!p.producto);
    }, [orden]);

    const paquetesOrden = useMemo(() => {
        const lista = Array.isArray(orden?.productos) ? orden.productos : [];
        return lista.filter(p => !p.producto && !!p.orden_salida_paquete);
    }, [orden]);

    const [cargandoRecetas, setCargandoRecetas] = useState(false);
    const [componentesPorPaquete, setComponentesPorPaquete] = useState({});

    const [filasPorFila, setFilasPorFila] = useState({});
    const [organizadoresPorFila, setOrganizadoresPorFila] = useState({});
    const [ubicacionesPorFila, setUbicacionesPorFila] = useState({});
    const [guardando, setGuardando] = useState(false);

    const opcionesArea = useMemo(() => (areas?.listado || []).map(toOption), [areas]);

    /* Lista plana: cada entrada tiene una ubicación propia que asignar, ya sea un producto suelto
       o un componente de un paquete devuelto. */
    const filasOrigen = useMemo(() => {
        const lista = [];

        productosOrden.forEach((p) => {
            lista.push({
                key: p._id,
                productoId: p.producto?._id || null,
                productoNombre: p.producto?.nombre || 'Sin definir',
                paqueteNombre: null,
                ordenSalidaPaquete: p.orden_salida_paquete || null,
                cantidad: Number(p.cantidad || 0)
            });
        });

        paquetesOrden.forEach((p) => {
            const comps = componentesPorPaquete[p._id] || [];
            comps.forEach((c) => {
                lista.push({
                    key: `${p._id}__${c.productoId}`,
                    productoId: c.productoId,
                    productoNombre: c.productoNombre,
                    paqueteNombre: p.paquete?.nombre || 'Paquete completo',
                    ordenSalidaPaquete: p.orden_salida_paquete,
                    cantidad: c.necesaria
                });
            });
        });

        return lista;
    }, [productosOrden, paquetesOrden, componentesPorPaquete]);

    useEffect(() => {
        if (!show) {
            setFilasPorFila({});
            setOrganizadoresPorFila({});
            setUbicacionesPorFila({});
            setComponentesPorPaquete({});
            return;
        }

        dispatch(buscarAreasConfiguracion());

        const cargarRecetas = async () => {
            setCargandoRecetas(true);

            const mapa = {};
            await Promise.all(paquetesOrden.map(async (p) => {
                const idPaqueteCatalogo = p.paquete?._id;
                if (!idPaqueteCatalogo) {
                    mapa[p._id] = [];
                    return;
                }

                const detalle = await dispatch(obtenerPaquete(idPaqueteCatalogo));
                const comps = Array.isArray(detalle?.productos) ? detalle.productos : [];
                const cantidadPaquetes = Number(p.cantidad || 0);

                mapa[p._id] = comps.map((c) => {
                    const conversion = Number(c?.conversion || 1) || 1;
                    return {
                        productoId: c?.producto?._id,
                        productoNombre: c?.producto?.nombre || 'Sin definir',
                        necesaria: (Number(c?.cantidad || 0) * cantidadPaquetes) / conversion
                    };
                });
            }));

            setComponentesPorPaquete(mapa);
            setCargandoRecetas(false);
        };

        cargarRecetas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, dispatch]);

    useEffect(() => {
        if (!show || filasOrigen.length === 0) return;

        setFilasPorFila((prev) => {
            const siguiente = { ...prev };
            filasOrigen.forEach((f) => {
                if (!siguiente[f.key]) {
                    siguiente[f.key] = [filaVacia(f.key, f.cantidad)];
                }
            });
            return siguiente;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, filasOrigen]);

    const estadoPorFila = useMemo(() => {
        const map = {};

        filasOrigen.forEach((f) => {
            const filas = filasPorFila[f.key] || [];
            const asignado = filas.reduce((acc, fila) => acc + Number(fila.cantidad || 0), 0);
            const faltaArea = filas.some((fila) => Number(fila.cantidad || 0) > 0 && !fila.area?.value);

            map[f.key] = {
                filas,
                asignado,
                completo: asignado === f.cantidad && f.cantidad > 0 && !faltaArea
            };
        });

        return map;
    }, [filasOrigen, filasPorFila]);

    const puedeConfirmar = !cargandoRecetas
        && filasOrigen.length > 0
        && filasOrigen.every((f) => estadoPorFila[f.key]?.completo);

    const handleAgregarFila = (key) => {
        setFilasPorFila((prev) => ({
            ...prev,
            [key]: [...(prev[key] || []), filaVacia(key, 0)]
        }));
    };

    const handleEliminarFila = (key, rowId) => {
        setFilasPorFila((prev) => ({
            ...prev,
            [key]: (prev[key] || []).filter((f) => f.rowId !== rowId)
        }));
    };

    const actualizarFila = (key, rowId, cambios) => {
        setFilasPorFila((prev) => ({
            ...prev,
            [key]: (prev[key] || []).map((f) => f.rowId === rowId ? { ...f, ...cambios } : f)
        }));
    };

    const handleSelectArea = async (key, rowId, opt) => {
        actualizarFila(key, rowId, { area: opt || null, organizador: null, ubicacion: null });

        setOrganizadoresPorFila((prev) => ({ ...prev, [rowId]: [] }));
        setUbicacionesPorFila((prev) => ({ ...prev, [rowId]: [] }));

        if (!opt?.value) return;

        const res = await dispatch(buscarOrganizadoresConfiguracion(opt.value));
        setOrganizadoresPorFila((prev) => ({ ...prev, [rowId]: res?.organizadores || [] }));
    };

    const handleSelectOrganizador = async (key, rowId, opt) => {
        actualizarFila(key, rowId, { organizador: opt || null, ubicacion: null });

        setUbicacionesPorFila((prev) => ({ ...prev, [rowId]: [] }));

        if (!opt?.value) return;

        const res = await dispatch(buscarUbicacionesConfiguracion(opt.value));
        setUbicacionesPorFila((prev) => ({ ...prev, [rowId]: res?.ubicaciones || [] }));
    };

    const handleSelectUbicacion = (key, rowId, opt) => {
        actualizarFila(key, rowId, { ubicacion: opt || null });
    };

    const handleChangeCantidad = (key, rowId, value) => {
        const v = value === '' ? '' : Math.max(0, Number(value));
        actualizarFila(key, rowId, { cantidad: v });
    };

    const handleConfirmar = async () => {
        if (!puedeConfirmar || !orden?._id) return;

        const productosDestino = [];

        filasOrigen.forEach((f) => {
            const filas = filasPorFila[f.key] || [];

            filas.forEach((fila) => {
                const cant = Number(fila.cantidad || 0);
                if (cant <= 0) return;

                productosDestino.push({
                    producto: f.productoId,
                    area: fila.area?.value || null,
                    organizador: fila.organizador?.value || null,
                    ubicacion: fila.ubicacion?.value || null,
                    cantidad: cant,
                    orden_salida_paquete: f.ordenSalidaPaquete || null
                });
            });
        });

        setGuardando(true);
        const ok = await dispatch(cambiarStatusOrden(orden._id, 2, productosDestino));
        setGuardando(false);

        if (!ok) return;

        onHide();
        if (typeof onSuccess === 'function') onSuccess();
    };

    const renderFilasUbicacion = (f) => {
        const estado = estadoPorFila[f.key] || { filas: [], asignado: 0, completo: false };

        return (
            <>
                {estado.filas.map((fila) => (
                    <div key={fila.rowId} className="d-flex align-items-center gap-2 mb-2">
                        <div style={{ minWidth: 130, maxWidth: 160 }}>
                            <Select
                                options={opcionesArea}
                                value={fila.area}
                                onChange={(opt) => handleSelectArea(f.key, fila.rowId, opt)}
                                isClearable
                                placeholder="Área"
                                noOptionsMessage={() => 'Sin resultados'}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                        <div style={{ minWidth: 130, maxWidth: 160 }}>
                            <Select
                                options={(organizadoresPorFila[fila.rowId] || []).map(toOption)}
                                value={fila.organizador}
                                onChange={(opt) => handleSelectOrganizador(f.key, fila.rowId, opt)}
                                isClearable
                                isDisabled={!fila.area}
                                placeholder="Organizador"
                                noOptionsMessage={() => 'Sin resultados'}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                        <div style={{ minWidth: 130, maxWidth: 160 }}>
                            <Select
                                options={(ubicacionesPorFila[fila.rowId] || []).map(toOption)}
                                value={fila.ubicacion}
                                onChange={(opt) => handleSelectUbicacion(f.key, fila.rowId, opt)}
                                isClearable
                                isDisabled={!fila.organizador}
                                placeholder="Ubicación"
                                noOptionsMessage={() => 'Sin resultados'}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: 80 }}
                            value={fila.cantidad}
                            min={0}
                            onChange={(e) => handleChangeCantidad(f.key, fila.rowId, e.target.value)}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-1"
                            title="Eliminar fila"
                            onClick={() => handleEliminarFila(f.key, fila.rowId)}
                        >
                            <i className="fas fa-trash text-danger" />
                        </button>
                    </div>
                ))}

                <div className="d-flex align-items-center justify-content-between">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleAgregarFila(f.key)}
                    >
                        <i className="feather icon-plus" /> Dividir en otra ubicación
                    </button>
                    <div className={estado.completo ? 'text-success' : 'text-danger'} style={{ fontSize: 11 }}>
                        Asignado: {formatNumber(estado.asignado)} / {formatNumber(f.cantidad)}
                    </div>
                </div>
            </>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Completar devolución</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Indica en qué ubicación de inventario se recibirá cada producto devuelto. La cantidad asignada debe cubrir el total de cada
                    producto (o de cada componente, si el renglón viene de un paquete completo) y se sumará al inventario al confirmar.
                </div>

                {cargandoRecetas ? (
                    <div className="text-muted text-center py-3">Cargando composición de los paquetes...</div>
                ) : (
                    <div className="table-responsive">
                        <Table bordered hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th style={{ width: 90 }} className="text-center">Cantidad</th>
                                    <th style={{ width: 700 }}>Ubicación destino</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filasOrigen.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted">Sin productos</td>
                                    </tr>
                                )}

                                {filasOrigen.map((f) => {
                                    const estado = estadoPorFila[f.key] || { filas: [], asignado: 0, completo: false };
                                    const conFlicto = !estado.completo;

                                    return (
                                        <tr key={f.key} className={conFlicto ? 'table-danger' : ''}>
                                            <td className="align-middle">
                                                {f.productoNombre}
                                                {f.paqueteNombre ? (
                                                    <div><span className="badge bg-secondary" style={{ fontSize: 10 }}>{f.paqueteNombre}</span></div>
                                                ) : null}
                                            </td>
                                            <td className="text-center align-middle">{formatNumber(f.cantidad)}</td>
                                            <td>{renderFilasUbicacion(f)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {!cargandoRecetas && !puedeConfirmar && (
                    <div className="mt-3">
                        <Badge bg="danger">Asigna una ubicación y completa la cantidad de cada producto antes de confirmar</Badge>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={guardando}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirmar} disabled={!puedeConfirmar || guardando}>
                    {guardando ? 'Confirmando...' : 'Confirmar devolución'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CompletarOrdenDevolucionModal;
